import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Card, Col, Form, Input, InputNumber, message, Modal, Popconfirm,
  Row, Space, Switch, Table, Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import useAuthStore from '../store/authStore';
import {
  Service, ServicePayload,
  listServices, createService, updateService, archiveService,
} from '../api/serviceCatalog';
import ServiceInfoDrawer from '../components/ServiceInfoDrawer';

const { Title } = Typography;

const ServiceCatalogPage: React.FC = () => {
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('orders:write');

  const [items, setItems] = useState<Service[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerId, setDrawerId] = useState<number | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Service | null>(null);
  const [form] = Form.useForm<ServicePayload>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listServices({ page, per_page: perPage, search: search || undefined, include_inactive: includeInactive });
      setItems(data.items);
      setTotal(data.total);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить список');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, includeInactive]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, unit: 'шт', sort_order: 0 });
    setModalOpen(true);
  };

  const openEdit = (s: Service) => {
    setEditing(s);
    form.setFieldsValue({
      name: s.name, default_price: s.default_price ?? null, unit: s.unit,
      default_warranty_days: s.default_warranty_days ?? null, notes: s.notes ?? null,
      sort_order: s.sort_order, is_active: s.is_active,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload: ServicePayload = {
        ...values,
        default_price: values.default_price ?? null,
        default_warranty_days: values.default_warranty_days ?? null,
      };
      if (editing) {
        await updateService(editing.id, payload);
        message.success('Услуга обновлена');
      } else {
        await createService(payload);
        message.success('Услуга добавлена');
      }
      setModalOpen(false);
      load();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || 'Не удалось сохранить');
    }
  };

  const handleArchive = async (s: Service) => {
    try {
      await archiveService(s.id);
      message.success('Услуга перенесена в архив');
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось удалить');
    }
  };

  const columns = useMemo(() => [
    { title: 'Наименование', dataIndex: 'name', key: 'name' },
    {
      title: 'Цена', dataIndex: 'default_price', key: 'default_price', width: 140,
      render: (v: number | null, r: Service) =>
        v != null ? `${Number(v).toLocaleString('ru-RU')} ₽ / ${r.unit}` : <span style={{ color: '#999' }}>—</span>,
    },
    { title: 'Ед.', dataIndex: 'unit', key: 'unit', width: 70 },
    {
      title: 'Гарантия', dataIndex: 'default_warranty_days', key: 'default_warranty_days', width: 100,
      render: (v: number | null) => (v != null ? `${v} дн.` : <span style={{ color: '#999' }}>—</span>),
    },
    { title: '№', dataIndex: 'sort_order', key: 'sort_order', width: 60 },
    {
      title: '', key: 'actions', width: 90, fixed: 'right' as const,
      onCell: () => ({ onClick: (e: React.MouseEvent) => e.stopPropagation() }),
      render: (_: unknown, s: Service) => (
        <Space size="small">
          {canWrite && <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(s)} />}
          {canWrite && s.is_active && (
            <Popconfirm title="Перенести в архив?" onConfirm={() => handleArchive(s)} okText="Да" cancelText="Нет">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ], [canWrite]);

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[8, 8]}>
        <Col><Title level={4} style={{ margin: 0 }}>Каталог услуг</Title></Col>
        <Col>
          <Space wrap>
            <Input.Search
              placeholder="Поиск по названию"
              allowClear
              onSearch={(val) => { setPage(1); setSearch(val); }}
              style={{ width: 260 }}
            />
            <Space size={6}>
              <span>Архивные</span>
              <Switch checked={includeInactive} onChange={(v) => { setPage(1); setIncludeInactive(v); }} />
            </Space>
            {canWrite && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Добавить</Button>
            )}
          </Space>
        </Col>
      </Row>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={items}
        loading={loading}
        onRow={(s) => ({
          onClick: () => { setDrawerId(s.id); setDrawerOpen(true); },
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page, pageSize: perPage, total, showSizeChanger: false,
          onChange: (p) => setPage(p),
        }}
        rowClassName={(s) => (s.is_active ? '' : 'row-archived')}
      />

      <ServiceInfoDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        serviceId={drawerId}
        width={460}
        extra={canWrite && drawerId ? (
          <Button type="primary" onClick={() => {
            const s = items.find((x) => x.id === drawerId);
            if (s) { setDrawerOpen(false); openEdit(s); }
          }}>Редактировать</Button>
        ) : null}
      />

      <Modal
        title={editing ? 'Редактировать услугу' : 'Новая услуга'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText="Сохранить"
        cancelText="Отмена"
        width={560}
      >
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item name="name" label="Наименование" rules={[{ required: true, message: 'Обязательное поле' }]}>
            <Input placeholder="Замена масла" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="default_price" label="Цена по умолчанию, ₽">
                <InputNumber min={0} step={50} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="unit" label="Единица">
                <Input placeholder="шт / ч" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="default_warranty_days" label="Гарантия, дн.">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="sort_order" label="Порядок">
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="notes" label="Примечание">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Card>
  );
};

export default ServiceCatalogPage;
